import React, { useEffect, useState } from "react";
import api, { formatINR } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Truck, FileDown, FileText, Upload, Search, Eye, Trash2, Pencil } from "lucide-react";
import { toast, Toaster } from "sonner";
import TransportCopyUploadDialog from "../components/TransportCopyUploadDialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const STATUS_COLORS = {
    placed: "bg-stone-100 text-stone-800",
    confirmed: "bg-sky-100 text-sky-800",
    packing: "bg-amber-100 text-amber-800",
    packed: "bg-amber-100 text-amber-800",
    shipped: "bg-emerald-100 text-emerald-800",
    delivered: "bg-emerald-100 text-emerald-800",
    cancelled: "bg-red-100 text-red-800",
};

export default function OrderStatus() {
    const { user } = useAuth();
    const [orders, setOrders] = useState([]);
    const [status, setStatus] = useState("all");
    const [orderType, setOrderType] = useState("all");
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [uploadDefaultInvoice, setUploadDefaultInvoice] = useState("");

    const [forbidden, setForbidden] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);  // order to delete
    const [busyId, setBusyId] = useState(null);

    const canManage = user?.role === "admin" || user?.role === "editor";

    // Build a role-aware detail link so admins land on /admin/* and employees on /employee/*.
    const basePath = canManage
        ? "/admin/order-status"
        : "/employee/order-status";
    const detailHref = (orderId) => `${basePath}/${orderId}`;

    const changeStatus = async (o, next) => {
        if (next === o.status) return;
        setBusyId(o.id);
        try {
            await api.put(`/admin/orders/${o.id}/status`, null, { params: { status: next } });
            toast.success(`Order #${o.id.slice(-8).toUpperCase()} → ${next}`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Status update failed");
        } finally {
            setBusyId(null);
        }
    };

    const deleteOrder = async () => {
        if (!confirmDelete) return;
        const o = confirmDelete;
        setBusyId(o.id);
        try {
            await api.delete(`/admin/orders/${o.id}`);
            toast.success(`Order #${o.id.slice(-8).toUpperCase()} deleted`);
            setConfirmDelete(null);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Delete failed");
        } finally {
            setBusyId(null);
        }
    };

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (status !== "all") params.status = status;
            if (orderType !== "all") params.order_type = orderType;
            if (q.trim()) params.q = q.trim();
            const { data } = await api.get("/orders-staff", { params });
            setOrders(data);
            setForbidden(false);
        } catch (e) {
            if (e.response?.status === 403) {
                setForbidden(true);
            } else {
                toast.error(e.response?.data?.detail || "Couldn't load orders");
            }
            setOrders([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, orderType]);
    // Debounce search
    useEffect(() => {
        const id = setTimeout(load, q.trim() ? 400 : 0);
        return () => clearTimeout(id);
        // eslint-disable-next-line
    }, [q]);

    const exportXlsx = async () => {
        try {
            const params = {};
            if (status !== "all") params.status = status;
            if (orderType !== "all") params.order_type = orderType;
            if (q.trim()) params.q = q.trim();
            const res = await api.get("/orders-staff-export.xlsx", { params, responseType: "blob" });
            const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `orders_${new Date().toISOString().slice(0, 10)}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Export failed");
        }
    };

    const uploadForInvoice = (invoiceNumber) => {
        setUploadDefaultInvoice(invoiceNumber || "");
        setUploadOpen(true);
    };

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-4" data-testid="order-status-page">
            <Toaster position="top-center" />
            {forbidden ? (
                <div className="bg-amber-50 border border-amber-300 p-8 text-center space-y-3" data-testid="order-status-forbidden">
                    <div className="font-serif text-2xl text-amber-900">Order status access restricted</div>
                    <p className="text-sm text-amber-800 max-w-xl mx-auto">
                        Only admins, editors, MRs and the <strong>Accounts &amp; Billing</strong> designation can view the dispatch tracker. If you believe you should have access, ask an admin to set your designation accordingly.
                    </p>
                </div>
            ) : (
                <>
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <div className="label-eyebrow text-sky-700">Dispatch & status</div>
                    <h1 className="font-serif text-4xl text-stone-900 mt-1">Order status</h1>
                    <p className="text-sm text-stone-600 mt-2 max-w-2xl">
                        Every order placed on the site — retail customers, MR-placed, distributor — with live packing &amp; dispatch info. Upload a transport copy by bill number to auto-attach it to the right order.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button onClick={() => uploadForInvoice("")} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="open-transport-upload">
                        <Truck className="w-4 h-4 mr-2" />Upload transport copy
                    </Button>
                    <Button onClick={exportXlsx} variant="outline" className="rounded-none border-stone-400" data-testid="export-xlsx">
                        <FileDown className="w-4 h-4 mr-2" />Excel
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white border border-stone-200 p-4 grid sm:grid-cols-4 gap-3">
                <div>
                    <Label className="text-xs uppercase tracking-widest text-stone-500">Status</Label>
                    <Select value={status} onValueChange={setStatus}>
                        <SelectTrigger className="rounded-none border-stone-300 mt-1" data-testid="filter-status"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            <SelectItem value="placed">Placed</SelectItem>
                            <SelectItem value="confirmed">Confirmed</SelectItem>
                            <SelectItem value="packing">Packing</SelectItem>
                            <SelectItem value="packed">Packed</SelectItem>
                            <SelectItem value="shipped">Shipped</SelectItem>
                            <SelectItem value="delivered">Delivered</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-widest text-stone-500">Order type</Label>
                    <Select value={orderType} onValueChange={setOrderType}>
                        <SelectTrigger className="rounded-none border-stone-300 mt-1" data-testid="filter-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All types</SelectItem>
                            <SelectItem value="retail">Retail</SelectItem>
                            <SelectItem value="distributor">Distributor</SelectItem>
                            <SelectItem value="mr">MR (field)</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="sm:col-span-2">
                    <Label className="text-xs uppercase tracking-widest text-stone-500">Search</Label>
                    <div className="relative mt-1">
                        <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Customer name, email, phone, city, state…" className="pl-9 rounded-none border-stone-300" data-testid="filter-q" />
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white border border-stone-200 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-stone-50 border-b border-stone-200">
                            <Th>Order #</Th>
                            <Th>Date</Th>
                            <Th>Type</Th>
                            <Th>Customer</Th>
                            <Th>City / State</Th>
                            <Th>Items</Th>
                            <Th cls="text-right">Total</Th>
                            <Th>Status</Th>
                            <Th>Invoice</Th>
                            <Th>Transport</Th>
                            <Th cls="text-right">Actions</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={11} className="p-8 text-center text-stone-500">Loading…</td></tr>
                        ) : orders.length === 0 ? (
                            <tr><td colSpan={11} className="p-12 text-center text-stone-500">No orders match the current filters.</td></tr>
                        ) : orders.map((o) => {
                            const tc = o.transport_copy;
                            return (
                                <tr key={o.id} className="border-b border-stone-100 hover:bg-sky-50/40" data-testid={`order-row-${o.id}`}>
                                    <td className="p-3 font-mono text-xs"><Link to={detailHref(o.id)} className="text-sky-700 hover:underline">#{o.id.slice(-8).toUpperCase()}</Link></td>
                                    <td className="p-3 font-mono text-xs text-stone-500">{(o.created_at || "").slice(0, 10)}</td>
                                    <td className="p-3 text-xs uppercase tracking-widest text-stone-500">{o.order_type}</td>
                                    <td className="p-3">
                                        <div className="font-medium text-stone-900">{o.address?.name || o.user_name || "—"}</div>
                                        {o.customer_email ? (
                                            <a href={`mailto:${o.customer_email}`} className="text-[11px] text-sky-700 hover:underline block" data-testid={`order-email-${o.id}`}>{o.customer_email}</a>
                                        ) : (
                                            <div className="text-[11px] text-stone-400" data-testid={`order-email-${o.id}`}>no email on file</div>
                                        )}
                                        <div className="text-[11px] text-stone-500">{o.address?.phone || ""}</div>
                                    </td>
                                    <td className="p-3 text-stone-700">{o.address?.city || "—"}{o.address?.state ? `, ${o.address.state}` : ""}</td>
                                    <td className="p-3 text-stone-600">{(o.items || []).length}</td>
                                    <td className="p-3 text-right font-mono">{formatINR(o.total)}</td>
                                    <td className="p-3"><Badge className={`rounded-none ${STATUS_COLORS[o.status] || "bg-stone-100"}`}>{o.status}</Badge></td>
                                    <td className="p-3 font-mono text-xs">{o.invoice_number || <span className="text-stone-400">—</span>}</td>
                                    <td className="p-3">
                                        {tc ? (
                                            <a href={tc.url} target="_blank" rel="noreferrer" className="inline-flex items-center text-emerald-700 hover:text-emerald-800 text-xs" data-testid={`tc-download-${o.id}`}>
                                                <FileText className="w-3.5 h-3.5 mr-1" />
                                                {tc.lr_number || tc.file_name?.slice(0, 14) || "Open"}
                                            </a>
                                        ) : o.invoice_number ? (
                                            <button onClick={() => uploadForInvoice(o.invoice_number)} className="text-sky-700 hover:underline text-xs inline-flex items-center" data-testid={`tc-upload-${o.id}`}>
                                                <Upload className="w-3.5 h-3.5 mr-1" />Upload
                                            </button>
                                        ) : (
                                            <span className="text-stone-400 text-xs">Need invoice</span>
                                        )}
                                    </td>
                                    <td className="p-3 text-right">
                                        <div className="inline-flex items-center gap-1 justify-end flex-wrap">
                                            <Link to={detailHref(o.id)} className="inline-flex items-center text-stone-600 hover:text-sky-700 text-xs px-2 py-1 border border-transparent hover:border-sky-200" data-testid={`order-view-${o.id}`} title="View / Print">
                                                <Eye className="w-3.5 h-3.5 mr-1" />View
                                            </Link>
                                            {canManage && (
                                                <>
                                                    <Select value={o.status} onValueChange={(v) => changeStatus(o, v)} disabled={busyId === o.id}>
                                                        <SelectTrigger className="w-[125px] h-7 rounded-none border-stone-300 text-xs" data-testid={`order-status-edit-${o.id}`}>
                                                            <Pencil className="w-3 h-3 mr-1 text-amber-600" />
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="placed">placed</SelectItem>
                                                            <SelectItem value="confirmed">confirmed</SelectItem>
                                                            <SelectItem value="packing">packing</SelectItem>
                                                            <SelectItem value="packed">packed</SelectItem>
                                                            <SelectItem value="shipped">shipped</SelectItem>
                                                            <SelectItem value="delivered">delivered</SelectItem>
                                                            <SelectItem value="cancelled">cancelled</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                    <button
                                                        onClick={() => setConfirmDelete(o)}
                                                        disabled={busyId === o.id}
                                                        className="inline-flex items-center text-red-700 hover:bg-red-50 text-xs px-2 py-1 border border-red-300 disabled:opacity-50"
                                                        data-testid={`order-delete-${o.id}`}
                                                        title="Delete order"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <TransportCopyUploadDialog
                open={uploadOpen}
                onOpenChange={setUploadOpen}
                defaultInvoiceNumber={uploadDefaultInvoice}
                onUploaded={() => load()}
            />

            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent className="rounded-none">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this order?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Order <span className="font-mono">#{confirmDelete?.id.slice(-8).toUpperCase()}</span>{" "}
                            for <strong>{confirmDelete?.address?.name || confirmDelete?.user_name || "—"}</strong>{" "}
                            (<span className="font-mono">{formatINR(confirmDelete?.total)}</span>) will be permanently removed.
                            {confirmDelete?.invoice_number && (
                                <span className="block mt-2 text-amber-700">
                                    The linked invoice <strong className="font-mono">{confirmDelete.invoice_number}</strong> will be kept (just unlinked) so the books stay clean. Delete it separately from Accounts → Invoices if needed.
                                </span>
                            )}
                            <span className="block mt-2 text-xs">This action is logged for admins.</span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={deleteOrder} className="bg-red-600 hover:bg-red-700 rounded-none" data-testid="delete-confirm">
                            Delete order
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
                </>
            )}
        </div>
    );
}

const Th = ({ children, cls = "" }) => (
    <th className={`text-left p-3 text-xs uppercase tracking-widest text-stone-500 ${cls}`}>{children}</th>
);
